import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Building2, Ruler, ShieldCheck, Home, Droplets, 
  UtensilsCrossed, ArrowRight, CheckCircle2 
} from "lucide-react";

const commercialServices = [
  {
    id: "polyurethane_blockwork",
    name: "Polyurethane Blockwork",
    description: "Professional polyurethane sealing for commercial blockwork construction. We ensure weather-tight and durable joints that withstand the harsh Queensland climate, protecting your building from water ingress and structural damage.",
    icon: Building2,
    features: [
      "High-performance polyurethane sealants",
      "Weather-resistant joints",
      "Long-lasting protection",
      "Suitable for all blockwork types"
    ]
  },
  {
    id: "render_expansion_joints",
    name: "Render Expansion Joints",
    description: "Expert sealing of render expansion joints to prevent cracking and water ingress in commercial facades. Our precision application ensures seamless integration with existing render finishes.",
    icon: Ruler,
    features: [
      "Prevents facade cracking",
      "Colour-matched sealants",
      "Movement accommodation",
      "Professional finish"
    ]
  },
  {
    id: "fire_rated_caulking",
    name: "Fire Rated Caulking & Certification",
    description: "Certified fire-rated caulking installation with full documentation and compliance certification. Essential for commercial buildings requiring fire compartmentation and Building Code compliance.",
    icon: ShieldCheck,
    features: [
      "BCA compliant installations",
      "Full certification provided",
      "Penetration sealing",
      "Regular inspection available"
    ]
  },
  {
    id: "internal_silicone",
    name: "Internal Silicone Works",
    description: "Complete internal silicone solutions for commercial fitouts including wet areas, kitchens, and general applications. We handle projects from single tenancies to entire buildings.",
    icon: Home,
    features: [
      "Bathroom & wet areas",
      "Kitchen installations",
      "General internal sealing",
      "Anti-mould options"
    ]
  },
];

const residentialServices = [
  {
    id: "bathroom_caulking",
    name: "Bathroom Caulking",
    description: "Professional bathroom silicone replacement and waterproofing. We replace old, mouldy silicone with fresh, anti-fungal sealant to protect your bathroom from water damage.",
    icon: Droplets,
    features: [
      "Mould removal",
      "Anti-fungal silicone",
      "Shower & bath sealing",
      "Basin surrounds"
    ]
  },
  {
    id: "kitchen_caulking",
    name: "Kitchen Caulking",
    description: "Kitchen benchtop and splashback sealing to prevent water damage and maintain hygiene. Keep your kitchen looking fresh and protected with professional caulking.",
    icon: UtensilsCrossed,
    features: [
      "Benchtop sealing",
      "Splashback joints",
      "Sink surrounds",
      "Hygienic finish"
    ]
  },
  {
    id: "structural_control_joints",
    name: "Structural Control Joints",
    description: "Professional sealing of structural control joints to manage building movement and prevent cracking. Essential for maintaining structural integrity and appearance.",
    icon: Ruler,
    features: [
      "Movement accommodation",
      "Crack prevention",
      "Long-lasting sealants",
      "Professional finish"
    ]
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-[#0F172A] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-[#F59E0B] font-medium text-sm uppercase tracking-wider mb-4">
            What We Do
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-6">
            Our Services
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg">
            From commercial high-rises to residential renovations, Gap Pro delivers 
            professional caulking solutions across the Gold Coast and South East Queensland.
          </p>
        </div>
      </section>

      {/* Commercial Services */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-16 h-1 bg-[#F59E0B]"></div>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-[#0F172A] uppercase">
              Commercial Services
            </h2>
            <span className="text-xs bg-[#0F172A] text-white px-3 py-1 rounded-full font-medium">
              Our Specialty
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {commercialServices.map((service) => (
              <Card 
                key={service.id} 
                className="border-0 shadow-lg hover:shadow-xl transition-shadow"
                data-testid={`service-detail-${service.id}`}
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-[#0F172A] rounded-xl flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-8 h-8 text-[#F59E0B]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-bold text-[#0F172A] uppercase mb-3">
                        {service.name}
                      </h3>
                      <p className="text-slate-600 mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      <ul className="grid grid-cols-2 gap-2">
                        {service.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle2 className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Residential Services */}
      <section className="py-16 lg:py-24 bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-16 h-1 bg-[#64748B]"></div>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-[#0F172A] uppercase">
              Residential Services
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {residentialServices.map((service) => (
              <Card 
                key={service.id} 
                className="border-0 shadow-lg hover:shadow-xl transition-shadow"
                data-testid={`service-detail-${service.id}`}
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-[#64748B]" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-[#0F172A] uppercase mb-3">
                    {service.name}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-[#64748B] flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white uppercase mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg mb-8">
            Contact us today for a free consultation and detailed quote for your project.
          </p>
          <Link to="/quote">
            <Button
              size="lg"
              className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0F172A] font-heading font-bold uppercase tracking-wide px-8"
              data-testid="services-cta-btn"
            >
              Request a Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
